import React from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Autocomplete,
  Box,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useStyles } from '../styles/teamFormModalStyles';
import Iconify from '../../../components/iconify';
import TeamSchema from '../schema';
import { ADD_TEAM_FORM_TITLE, EDIT_TEAM_FORM_TITLE, INITIAL_FORM_STATE } from '../constants';

const TeamFormModal = ({ open, onClose, onSubmit, selectedRow, action, allManagerList, employeeList }) => {
  const classes = useStyles();

  let initialValues;
  if (action === 'create') {
    initialValues = INITIAL_FORM_STATE;
  } else if (action === 'update') {
    const employeeIds = selectedRow?.teammembers?.map((member) => member?.user?.id);
    const team = employeeList?.filter((employee) => employeeIds.includes(employee?.id));
    initialValues = { managerId: selectedRow?.managerId, team };
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: TeamSchema,
    onSubmit: handleSubmit,
  });

  const { values, submitForm, isSubmitting, errors, touched, setFieldValue, getFieldProps, resetForm } = formik;

  const handleClose = () => {
    onClose();
    resetForm({ values: INITIAL_FORM_STATE });
  };

  const selectedMangerOption = allManagerList.find((manager) => manager.id === values?.managerId);
  const managersWithoutTeam = allManagerList.filter((manager) => manager?.teams?.length === 0);
  const managerList = action !== 'create' ? [selectedMangerOption] : managersWithoutTeam;

  let headerText = '';
  if (action === 'create') {
    headerText = ADD_TEAM_FORM_TITLE;
  } else if (action === 'update') {
    headerText = EDIT_TEAM_FORM_TITLE;
  }

  return (
    <Modal className={classes.modal} open={open} onClose={handleClose}>
      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Box className={classes.modalHeader}>
            <Typography variant="h5" className={classes.headerText}>
              {headerText}
            </Typography>
            <IconButton color="primary" onClick={handleClose}>
              <Iconify icon="ic:round-clear" />
            </IconButton>
          </Box>
          <Divider className={classes?.divider} />
          <Container maxWidth="sm">
            <FormikProvider value={formik}>
              <Form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <FormControl error={touched?.managerId && Boolean(errors?.managerId)} fullWidth>
                    <InputLabel>Manager</InputLabel>
                    <Select
                      name="manager"
                      id="manager"
                      disabled={action === 'update'}
                      {...getFieldProps('managerId')}
                      label="Manager"
                      onChange={(event, value) => {
                        setFieldValue('managerId', value?.props?.value);
                      }}
                    >
                      {managerList.map((row, i) => (
                        <MenuItem value={row?.id} key={i}>
                          {row?.userName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched?.managerId && errors?.managerId}</FormHelperText>
                  </FormControl>
                  <Autocomplete
                    multiple
                    {...getFieldProps('team')}
                    options={employeeList}
                    onChange={(event, value) => {
                      setFieldValue('team', value);
                    }}
                    fullWidth
                    limitTags={1}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="team"
                        label="Select Employees"
                        error={touched?.team && Boolean(errors?.team)}
                        helperText={touched?.team && errors?.team}
                      />
                    )}
                  />
                  <LoadingButton fullWidth size="large" onClick={submitForm} variant="contained" loading={isSubmitting}>
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            </FormikProvider>
          </Container>
        </Paper>
      </Box>
    </Modal>
  );
};

TeamFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  action: PropTypes.string.isRequired,
  allManagerList: PropTypes.array.isRequired,
  employeeList: PropTypes.array.isRequired,
};

export default TeamFormModal;
